import {errorToastOptions} from "../ErrorToast";
import {
    IonButton, IonButtons,
    IonContent, IonFab, IonFabButton, IonHeader, IonIcon, IonInput,
    IonItem,
    IonItemDivider,
    IonLabel, IonList, IonModal,
    IonSearchbar, IonTitle, IonToggle, IonToolbar, useIonAlert,
    useIonToast,
    useIonViewWillEnter
} from "@ionic/react";
import AdminEntityList from "./AdminEntityList";
import React, {useRef, useState} from "react";
import {createUserName, getUser, updateUserName} from "../../repositories/adminUserRepository";
import {Access} from "../../entities/Access";
import moment from "moment/moment";
import PermissionRepositoryTypeIcon from "../PermissionRepositoryTypeIcon";
import {add} from "ionicons/icons";
import AdminServiceTypeTypeahead from "./AdminServiceTypeTypeahead";
import AdminUserNameTypeahead from "./AdminUserNameTypeahead";

export const AdminManageUserNames = ({user, userNames, setUserNames, reloadUserNames}) => {
    const [selectedUserName, setSelectedUserName] = useState(null);
    const [editedUserName, setEditedUserName] = useState({});
    const [newUserName, setNewUserName] = useState({userId: user.id});
    const [search, setSearch] = useState('');
    const [presentToast, dismissToast] = useIonToast();
    const [presentAlert, dismissAlert] = useIonAlert();
    const modal = useRef();

    const presentError = async (e, fallbackMessage) => {
        const message = typeof e.response?.data === 'string' && e.response.data ? e.response.data : fallbackMessage;
        await presentToast(errorToastOptions(message));
    }

    const openUserNameEditModal = (userName) => () => {
        setSelectedUserName(userName);
        setEditedUserName({useEntraId: !!userName.useEntraId, userName: userName.userName ?? ''});
    }

    const saveUserName = async () => {
        try {
            await updateUserName({
                userId: selectedUserName.userId,
                serviceTypeId: selectedUserName.serviceTypeId,
                userName: editedUserName.userName || null,
                useEntraId: editedUserName.useEntraId
            });
        } catch (e) {
            await presentError(e, "Failed to save username");
            return;
        }
        setSelectedUserName(null);
        await presentToast({message: "Successfully saved username", duration: 2000, color: "success"});
        await reloadUserNames();
    }


    async function confirm() {
        try {
            await createUserName({
                userId: newUserName.userId,
                serviceTypeId: +newUserName.serviceType.id,
                userName: newUserName.useEntraId ? (newUserName.entraObjectId || null) : newUserName.userName.title,
                useEntraId: !!newUserName.useEntraId
            });
        } catch (e) {
            await presentError(e, "Failed to create username");
            return;
        }
        modal.current?.dismiss(newUserName, 'confirm');
        await presentToast({message: "Successfully created username", duration: 2000, color: "success"});
        await reloadUserNames();
    }

    function cancel() {
        presentAlert({
            header: 'Confirmation',
            message: 'Are you sure you want to discard the changes?',
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                    handler: () => {
                        console.log('Cancel clicked');
                    }
                },
                {
                    text: 'Confirm',
                    handler: () => {
                        modal.current?.dismiss()
                    }
                }
            ]
        })
    }

    const normalizedSearch = search.trim().toLowerCase();
    const filteredUserNames = normalizedSearch
        ? (userNames ?? []).filter(userName =>
            [userName.serviceType.serviceTypeName, userName.serviceType.permissionRepositoryType, userName.userName]
                .some(value => value?.toLowerCase().includes(normalizedSearch)))
        : userNames;

    async function onCreateModalWillDismiss() {
        setNewUserName({userId: user.id})
    }

    return (
        <div>
            <IonItemDivider sticky className="usernames-list-search-bar">
                <IonButton className="ion-margin-start" fill="outline" slot="start" id="open-modal">
                    <IonIcon icon={add} />
                </IonButton>
                <IonSearchbar
                    placeholder="Search service user names"
                    debounce={300}
                    mode="ios"
                    enterkeyhint="search" inputmode="search" onIonInput={(ev) => setSearch(ev.detail.value ?? '')}></IonSearchbar>
            </IonItemDivider>
            <IonList>
                {filteredUserNames?.length === 0 && (
                    <IonItem lines="none">
                        <IonLabel color="medium">
                            {userNames?.length ? 'No user names match the search' : `${user.name} has no service user names`}
                        </IonLabel>
                    </IonItem>
                )}
                {filteredUserNames?.map((userName, index) => (
                    <IonItem lines={index === filteredUserNames.length - 1 ? "none" : "inset"} key={userName.serviceTypeId} button onClick={openUserNameEditModal(userName)}>
                        <PermissionRepositoryTypeIcon slot="start" type={userName.serviceType.permissionRepositoryType} />
                        <IonLabel>
                            <h3>{ userName.serviceType.serviceTypeName }</h3>
                            <p>{ userName.useEntraId ? `Entra ID: ${userName.userName ?? 'resolved by email on first access'}` : userName.userName }</p>
                        </IonLabel>
                    </IonItem>
                ))}
            </IonList>

            <IonModal isOpen={selectedUserName !== null}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Edit username</IonTitle>
                        <IonButtons slot="start">
                            <IonButton onClick={() => setSelectedUserName(null)}>Cancel</IonButton>
                        </IonButtons>
                        <IonButtons slot="end">
                            <IonButton
                                disabled={!editedUserName.useEntraId && !editedUserName.userName}
                                strong={true}
                                onClick={() => saveUserName()}>
                                Save
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    <IonItem>
                        <IonLabel>
                            <h3>{ selectedUserName?.serviceType.serviceTypeName }</h3>
                        </IonLabel>
                    </IonItem>
                    <IonItem>
                        <IonToggle checked={!!editedUserName.useEntraId}
                                   onIonChange={(ev) => setEditedUserName(x => ({...x, useEntraId: ev.detail.checked}))}>
                            Use Entra ID
                        </IonToggle>
                    </IonItem>
                    <IonItem>
                        <IonInput label={editedUserName.useEntraId ? "Entra ID object id" : "Service user name"}
                                  labelPlacement="stacked"
                                  placeholder={editedUserName.useEntraId ? "Leave empty to resolve by email" : "Enter service user name"}
                                  value={editedUserName.userName}
                                  onIonInput={(ev) => setEditedUserName(x => ({...x, userName: ev.target.value}))}/>
                    </IonItem>
                </IonContent>
            </IonModal>

            <IonModal ref={modal} trigger="open-modal" onWillDismiss={(ev) => onCreateModalWillDismiss(ev)}>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton onClick={() => cancel()}>Cancel</IonButton>
                        </IonButtons>
                        <IonTitle>Create username</IonTitle>
                        <IonButtons slot="end">
                            <IonButton
                                disabled={typeof newUserName.serviceType === "undefined" || (!newUserName.useEntraId && typeof newUserName.userName === "undefined")}
                                strong={true}
                                onClick={() => confirm()}>
                                Confirm
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    <AdminServiceTypeTypeahead
                        value={newUserName.serviceType}
                        setValue={(serviceType) => setNewUserName(x => ({...x, serviceType }))} />
                    <IonItem>
                        <IonToggle checked={!!newUserName.useEntraId}
                                   onIonChange={(ev) => setNewUserName(x => ({...x, useEntraId: ev.detail.checked}))}>
                            Use Entra ID
                        </IonToggle>
                    </IonItem>
                    { newUserName.useEntraId
                        ? <IonItem>
                            <IonInput label="Entra ID object id"
                                      labelPlacement="stacked"
                                      placeholder="Leave empty to resolve by email"
                                      value={newUserName.entraObjectId}
                                      onIonInput={(ev) => setNewUserName(x => ({...x, entraObjectId: ev.target.value}))}/>
                        </IonItem>
                        : <AdminUserNameTypeahead disabled={typeof newUserName.serviceType === "undefined"}
                                                  serviceType={newUserName.serviceType}
                                                  value={newUserName.userName}
                                                  setValue={(userName) => setNewUserName(x => ({...x, userName}))}/> }
                </IonContent>
            </IonModal>
        </div>
    );
}